import React from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';

import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import OEIconCodes from '../../../lib/oe-icon-codes';
import retardUpdate from '../../../lib/react/update-retarder';
import OEResizeObserver from '../../../lib/components/oe-resize-observer';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import OEPopover from '../../views/oe-popover';
import {OEGroupControl, OEControl, OEIconButtonGroupControl} from '../../elements/controls/oe-controls';
import {OENoteModels} from './oe-note-model';
import OEObjectModelValueEdit from '../../elements/object-model-value-edit/oe-object-model-value-edit';
import {OENoteTransferPropertyBits, OENotePropertyTransferControllerBtnIds} from './oe-note-model';

export class OENotePropertyTransferController extends React.PureComponent {

    constructor(props)  {
        super(props);

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.bitKeyArray = Object.keys(OENoteTransferPropertyBits).filter(key => !['MAX', 'all', 'vAlignment', 'hAlignment', 'colors', 'defaultStyleTransfer', 'style', 'defaultPropTransfer'].includes(key));

        this.id = typeof(this.props.id) === 'number' ? this.props.id : null;

        this.transferBits = OENoteTransferPropertyBits.defaultPropTransfer;
        this.params = this.defaultParams();

        this.state = this.defaultState();
        
        this.updateLanguage = this.updateLanguage.bind(this);
        this.onUIControllerStateChanged = this.onUIControllerStateChanged.bind(this);

        this.onResize = this.onResize.bind(this);

        this.onParamsChange = this.onParamsChange.bind(this);
        this.onSelectDeselectStyleTransferBitsButtonPressed = this.onSelectDeselectStyleTransferBitsButtonPressed.bind(this);
        this.onTransferToUserRefButtonPressed = this.onTransferToUserRefButtonPressed.bind(this);
        this.onTransferToAllButtonPressed = this.onTransferToAllButtonPressed.bind(this);
    }

    transferBitsToParams(bits)  {
        let params = {};
        this.bitKeyArray.forEach(key => {
            params[key] = (bits & OENoteTransferPropertyBits[key]) == OENoteTransferPropertyBits[key];
        });
        return params;
    }

    transferBitsFromParams(params) {
        let bits = OENoteTransferPropertyBits.none;
        this.bitKeyArray.forEach(key => {
            if(params[key]) bits = bits | OENoteTransferPropertyBits[key];
        });
        return bits;
    }

    defaultParams() {
        return Object.assign({userRef: undefined}, this.transferBitsToParams(OENoteTransferPropertyBits.defaultPropTransfer));
    }

    defaultState()  {
        return {
            uiEnabled: false,
            transferBits: OENoteTransferPropertyBits.defaultPropTransfer,
            params: this.defaultParams(),
            strings: {
                paramsGroupLabel: '',
                transferGroupLabel: '',
                transferToUserRefBtnLabel: '',
                transferToAllBtnLabel: ''
            }
        };
    }

    componentWillReceiveProps(nextProps) {
        if(nextProps.id !== this.props.id)     {
            this.id = typeof(nextProps.id) === 'number' ? nextProps.id : null;
            this.updateParams();
        }
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);
    }

    onRelease()    {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);	
    }

    updateLanguage()   {
        this.setState({
            strings: {
                paramsGroupLabel:           this.oe.sharedInterface.getLocalizedStringEnc('note_property_transfer_view_group_params'),
                transferGroupLabel:         this.oe.sharedInterface.getLocalizedStringEnc('note_property_transfer_view_transfer_group'),
                transferToUserRefBtnLabel:  this.oe.sharedInterface.getLocalizedStringEnc('note_property_transfer_view_transfer_to_user_ref_btn'),
                transferToAllBtnLabel:      this.oe.sharedInterface.getLocalizedStringEnc('note_property_transfer_view_transfer_to_all_btn')
            }
        });
    }

    updateParams() {
        if(!this.oe.isReady() || this.id === null)    return;

        this.params.userRef = this.oe.sharedInterface.getUIControllerNote().getNoteUserRef(this.id);

        this.setState({params: clone(this.params)});
    }

    updateUIState()    {
        this.setState({
            uiEnabled: this.oe.sharedInterface.getUIControllerNote().getUIEnabled()
        });
    }

    updateState(released)   {
        if(!this.oe.isReady() || released)   {
            this.setState({uiEnabled: false});
            return;
        }

        retardUpdate(this, () => {
            this.updateLanguage();
            this.updateUIState();
            this.updateParams();
        });
    }

    onUIControllerStateChanged(message, userInfo)    {
        if(userInfo.type === this.oe.Module.UIControllerType.note) {
            this.updateUIState();
        }
    }

    onResize(sender, size) {
        if(this.props.onResize) this.props.onResize(this, size);
    }

    render()    {
        return (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                
                <div className={'note-property-transfer-controller ' + this.props.className}>

                    {!this.props.onResize ? null :
                        <OEResizeObserver onResize={this.onResize}/>
                    }

                    <OEGroupControl title={this.state.strings.paramsGroupLabel}>

                        <OEControl className="transfer-parameters-1">
                            <OEObjectModelValueEdit
                                moduleId={this.props.moduleId}
                                className="multi-column"
                                model={OENoteModels.propertyTransfer}
                                object={this.state.params}
                                onChange={this.onParamsChange}
                            />
                        </OEControl>

                        <OEIconButtonGroupControl
                            className="style-transfer-bits-btns"
                            buttons={[
                                {
                                    id: OENotePropertyTransferControllerBtnIds.deselectAllStyleTransferBits,
                                    icon: OEIconCodes.notePropertyTransferDeselectAllTransferBits,
                                    disabled: !(this.state.transferBits & OENoteTransferPropertyBits.defaultStyleTransfer),
                                },
                                {
                                    id: OENotePropertyTransferControllerBtnIds.selectAllStyleTransferBits,
                                    icon: OEIconCodes.notePropertyTransferSelectAllTransferBits,
                                    disabled: (this.state.transferBits & OENoteTransferPropertyBits.defaultStyleTransfer) == OENoteTransferPropertyBits.defaultStyleTransfer,
                                }
                            ]}
                            onPressed={this.onSelectDeselectStyleTransferBitsButtonPressed}
                        />

                        <OEControl className="transfer-parameters-2">
                            <OEObjectModelValueEdit
                                moduleId={this.props.moduleId}
                                className="multi-column"
                                model={OENoteModels.propertyTransfer2}
                                object={this.state.params}
                                onChange={this.onParamsChange}
                            />
                        </OEControl>

                    </OEGroupControl>

                    <OEGroupControl title={this.state.strings.transferGroupLabel}>

                        <OEIconButtonGroupControl
                            title={this.state.strings.transferToUserRefBtnLabel}
                            buttons={[
                                {
                                    id: OENotePropertyTransferControllerBtnIds.transferToUserRef,
                                    icon: OEIconCodes.notePropertyTransferToUserRef,
                                    disabled: !this.state.params.userRef || !this.state.uiEnabled || !(this.state.transferBits),
                                    iconClassName: "rotate-180"
                                }
                            ]}
                            onPressed={this.onTransferToUserRefButtonPressed}
                        />

                        <OEIconButtonGroupControl
                            title={this.state.strings.transferToAllBtnLabel}
                            buttons={[
                                {
                                    id: OENotePropertyTransferControllerBtnIds.transferToAll,
                                    icon: OEIconCodes.notePropertyTransferToAll,
                                    disabled: !this.state.uiEnabled || !(this.state.transferBits),
                                    iconClassName: "rotate-180"
                                }
                            ]}
                            onPressed={this.onTransferToAllButtonPressed}
                        />

                    </OEGroupControl>

                </div>
            </React.Fragment>
        );
    }

    onParamsChange(sender, params)    {
        this.transferBits = this.transferBitsFromParams(params);
        this.params = clone(params);

        this.setState({
            transferBits:   this.transferBits,
            params:         clone(this.params)
        });
    }

    onSelectDeselectStyleTransferBitsButtonPressed(sender, id)  {
        let transferBits = OEToolbox.binaryAddRemove(this.transferBits, OENoteTransferPropertyBits.defaultStyleTransfer, id === OENotePropertyTransferControllerBtnIds.deselectAllStyleTransferBits);
        if(transferBits == this.transferBits) return;

        this.transferBits = transferBits;
        this.params = Object.assign({...this.params}, this.transferBitsToParams(transferBits));

        this.setState({
            transferBits:   this.transferBits,
            params:         clone(this.params)
        });
    }

    onTransferToUserRefButtonPressed(sender, id) {
        if(!this.oe.isReady() || this.id === null || !this.params.userRef || !this.transferBits) return;

        this.oe.sharedInterface.getUIControllerNote().transferNoteProperties(this.id, this.params.userRef, this.transferBits);
    }

    onTransferToAllButtonPressed(sender, id)  {
        if(!this.oe.isReady() || this.id === null || !this.transferBits) return;

        this.oe.sharedInterface.getUIControllerNote().transferNotePropertiesToAll(this.id, this.transferBits);
    }
}

OENotePropertyTransferController.defaultProps = {
    moduleId:               '',
    className:              ''
};

OENotePropertyTransferController.propTypes = {
    moduleId:               PropTypes.string,
    className:              PropTypes.string,
    id:                     PropTypes.number,
    onResize:               PropTypes.func
}; 

export const OENotePropertyTransferPopoverController = OEPopover.coat(OENotePropertyTransferController, {
    noHeader:               true,
}, {
    placement:              'right',
    buttonClassName:        'transparent-btn',
    controllerClassName:    'note-property-transfer-popover compact'
}, {
    controllerClassName:    PropTypes.string
});